import React from 'react'
import { Box, Divider, Stack } from '@mui/material'
import {
  SectionHeader,
  SectionHeaderData,
  SectionWrapper,
} from '@components/common'
import { CustomStyles, getStyles } from '@utils/styles'
import { StatsCard, StatsCardData } from './StatsCard'
import defaultStyles, { StyleClassNames } from './styles'

export type RatingStatsSectionData = {
  statsData: StatsCardData[]
  sectionHeaderData?: SectionHeaderData
  featuredRating?: boolean
}

export type RatingStatsSectionDataProps = RatingStatsSectionData & {
  csx?: CustomStyles<StyleClassNames>
  alternateSectionBg?: boolean
}

export function RatingStatsSection({
  statsData,
  sectionHeaderData,
  featuredRating = false,
  csx,
  alternateSectionBg = false,
}: RatingStatsSectionDataProps) {
  const styles = getStyles(defaultStyles, csx)
  const hasStats = statsData.length > 0
  const [firstCard, ...restCards] = statsData

  const desktopStatsData = React.useMemo(() => {
    if (!featuredRating || statsData.length < 2) {
      return statsData
    }
    const [featured, ...rest] = statsData
    const centerIndex = Math.floor(statsData.length / 2)
    const reordered = [...rest]
    reordered.splice(centerIndex, 0, featured)
    return reordered
  }, [featuredRating, statsData])

  const mobileCardPairs = React.useMemo(() => {
    const pairs: StatsCardData[][] = []

    for (let index = 0; index < restCards.length; index += 2) {
      pairs.push(restCards.slice(index, index + 2))
    }

    return pairs
  }, [restCards])

  if (!hasStats) {
    return null
  }

  return (
    <SectionWrapper
      alternateSectionBg={alternateSectionBg}
      csx={{
        wrapper: styles('sectionWrapper').sx,
      }}
    >
      <Stack gap={4}>
        {sectionHeaderData && <SectionHeader {...sectionHeaderData} />}
        <Box
          {...styles('desktopContainer', {
            backgroundColor: 'common.white',
          })}
        >
          {desktopStatsData.map((stat, index) => (
            <StatsCard
              key={index}
              {...stat}
              isLast={index === desktopStatsData.length - 1}
            />
          ))}
        </Box>

        <Box
          {...styles('mobileContainer', {
            backgroundColor: 'common.white',
          })}
        >
          <Box {...styles('mobileStatsCard')}>
            <StatsCard {...firstCard} />
          </Box>
          {!!mobileCardPairs.length && (
            <Box {...styles('restCardWrapper')}>
              {mobileCardPairs.map((pair, pairIndex) => (
                <Box
                  key={pairIndex}
                  {...styles(
                    'restCardRow',
                    pair.length === 1
                      ? {
                          justifyContent: 'center',
                        }
                      : undefined
                  )}
                >
                  <Box {...styles('mobileStatsCard')}>
                    <StatsCard
                      {...pair[0]}
                      wrapperSx={{
                        gap: '0px',
                      }}
                    />
                  </Box>

                  {pair[1] && (
                    <>
                      <Divider orientation="vertical" flexItem />
                      <Box {...styles('mobileStatsCard')}>
                        <StatsCard
                          {...pair[1]}
                          wrapperSx={{
                            gap: '0px',
                          }}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Stack>
    </SectionWrapper>
  )
}
